import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { Card, CardContent } from '../../../../../components/ui/card';
import { FileText, Edit3, Save, X, Download, Plus, Trash2 } from 'lucide-react';
import type { SpecificationDocument } from '../../../types/index';
import { DocumentDownloadModal } from './DocumentDownloadModal';
import { SPEC_TYPE_LABELS } from '../../../constants/specificationConstants';
import { AddSpecification } from '../../../../../redux/Project/specificationSaga';
import customNotification, { Notification } from '../../../../../components/ui/custom-notification';
import { MultiSelect } from '../../../../../components/ui/multi-select';
import AddNewItemDialog from '../../../../../components/ui/AddNewItemDialog';
import type { DashboardProject } from '../../../types/project';
import { convertFitmentReadingsObject, convertFitmentReadingsObjectToInches, convertFitmentReadingsValueToInches, getUnitLabel, getUnitFromSettings } from '../../../../../utils/unitUtils';
import { AddMaterialCategory, getListMaterialCategory } from '../../../../../redux/Project/materialSaga';

interface SpecificationDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  specification: SpecificationDocument;
  onUpdate?: (updatedSpec: SpecificationDocument) => void;
  pipeDetails: SpecificationDocument[];
  project?: DashboardProject;
  canEdit?: boolean;
}

export const SpecificationDetailModal: React.FC<SpecificationDetailModalProps> = ({
  isOpen,
  onClose,
  specification,
  onUpdate,
  pipeDetails,
  project,
  canEdit = true
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedDetails, setEditedDetails] = useState<SpecificationDocument['details']>({});
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedSpecification, setSelectedSpecification] = useState<SpecificationDocument | null>(null);
  const [newPassName, setNewPassName] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [isAddingAlignmentLocation, setIsAddingAlignmentLocation] = useState(false);
  const [isAddingProcessType, setIsAddingProcessType] = useState(false);
  const [isAddingWeldPosition, setIsAddingWeldPosition] = useState(false);
  const [isAddingJointType, setIsAddingJointType] = useState(false);

  const [alignmentLocationOptions, setAlignmentLocationOptions] = useState([
    { value: 'Left End', label: 'Left End' },
    { value: 'Mid 1', label: 'Mid 1' },
    { value: 'Center', label: 'Center' },
    { value: 'Mid 2', label: 'Mid 2' },
    { value: 'Right End', label: 'Right End' }
  ]);
  const [noteOptions, setNoteOptions] = useState(
    'Internal Line Up Clamp was used for all fit ups. Beveled ends were verfied to be square. '
  );

  const [passOptions, setPassOptions] = useState([
    { value: 'Hot', label: 'Hot' },
    { value: 'Root', label: 'Root' },
    { value: 'Fill', label: 'Fill' },
    { value: 'Last Fill', label: 'Last Fill' },
    { value: 'Strip', label: 'Strip' },
    { value: 'Cap', label: 'Cap' },
    { value: 'Disabled', label: 'Disabled' }
  ]);
  // Default options for the multi-select fields
  const [processTypeOptions, setProcessTypeOptions] = useState<any[]>([]);
  const [weldPositionOptions, setWeldPositionOptions] = useState<any[]>([]);

  const [jointTypeOptions, setJointTypeOptions] = useState<any[]>([]);


  const [unit, setUnit] = useState<string>(() => project?.project?.unit || getUnitFromSettings() || 'in');
  useEffect(() => {
    setUnit(project?.project?.unit || getUnitFromSettings() || 'in');
  }, [project?.project?.unit]);

  const isBlankValue = (value: unknown) =>
    value === null || value === undefined || value === '';

  const toOptionalNumber = (value: string) =>
    value === '' ? null : Number(value);

  const displayValue = (value: unknown) =>
    isBlankValue(value) ? 'Not specified' : String(value);

  const hasSpoolsFitmentFields = (details: SpecificationDocument['details']) => {
    const fitmentReadings = details?.fitmentReadings;
    return Boolean(
      fitmentReadings?.wallThicknessTopRangeMin !== undefined ||
      fitmentReadings?.wallThicknessTopRangeMax !== undefined ||
      fitmentReadings?.gapToleranceMin !== undefined ||
      fitmentReadings?.gapToleranceMax !== undefined
    );
  };

  const isSpoolsSpecification = () =>
    (specification as any)?.projectSubType === 'spools' ||
    (specification as any)?.fabricationSubType === 'spools' ||
    hasSpoolsFitmentFields(editedDetails) ||
    Boolean(editedDetails?.preheatTemperature);

  useEffect(() => {

    setSelectedSpecification({
      ...specification,
      uploadDate: specification.uploadDate || new Date().toISOString(),
      uploadedBy: specification.uploadedBy || 'no email',


    });

    const sourceDetails = specification?.files?.[0]?.details || specification?.details || {};
    if (specification && sourceDetails) {
      const details = { ...sourceDetails };

      // Initialize passConfig if it doesn't exist
      if (!details.passConfig) {
        details.passConfig = {
          passes: []
        };
      }

      // Initialize passes array if it doesn't exist
      if (!details.passConfig.passes) {
        details.passConfig.passes = [];
      }

      // Initialize alignment if it doesn't exist
      if (!details.alignment) {
        details.alignment = {
          numberOfAxialAlignments: 2,
          alignmentLocation: [],
          note: [],
          degrees: [0, 90]
        };
      }

      // Initialize weldProcess if it doesn't exist
      if (!details.weldProcess) {
        details.weldProcess = {
          processType: [],
          weldPosition: [],
          jointType: []
        };
      }

      const values = {...details};
      if(values.fitmentReadings){
        console.log(values.fitmentReadings);
       const convertedValues = convertFitmentReadingsObject(values.fitmentReadings);
       values.fitmentReadings = convertedValues;
      }
      setEditedDetails(values);
    }
  }, [specification]);



   const onCloseModal = () => {
    setEditedDetails({});
    setIsEditing(false);
    onClose();
   }



  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Load departments and companies in parallel
        const [weldProcessList, weldPositionList, jointTypeList, alignmentLocationList] = await Promise.all([
          getListMaterialCategory('WELD_PROCESS_SPECS'),
          getListMaterialCategory('WELD_POSITION_SPECS'),
          getListMaterialCategory('JOINT_TYPE_SPECS'),
          getListMaterialCategory('ALIGNMENT_LOCATION_SPECS'),
        ]);


        // Remove duplicates and sort companies alphabetically by company_name
        const uniqueWeldProcessList = weldProcessList
          .filter((tag: any, index: number, self: any[]) =>
            index === self.findIndex((t: any) => t.tags_name === tag.tags_name)
          )
          .sort((a: any, b: any) => a.tags_name.localeCompare(b.tags_name));

        setProcessTypeOptions(uniqueWeldProcessList.map((tag: any) => ({ id: tag.tags_id, value: tag.tags_name, label: tag.tags_name })));

        const uniqueWeldPositionList = weldPositionList
          .filter((tag: any, index: number, self: any[]) =>
            index === self.findIndex((t: any) => t.tags_name === tag.tags_name)
          )
          .sort((a: any, b: any) => a.tags_name.localeCompare(b.tags_name));

        setWeldPositionOptions(uniqueWeldPositionList.map((tag: any) => ({ id: tag.tags_id, value: tag.tags_name, label: tag.tags_name })));

        const uniqueJointTypeList = jointTypeList
          .filter((tag: any, index: number, self: any[]) =>
            index === self.findIndex((t: any) => t.tags_name === tag.tags_name)
          )
          .sort((a: any, b: any) => a.tags_name.localeCompare(b.tags_name));

        setJointTypeOptions(uniqueJointTypeList.map((tag: any) => ({ id: tag.tags_id, value: tag.tags_name, label: tag.tags_name })));

        const uniqueAlignmentLocationList = alignmentLocationList
          .filter((tag: any, index: number, self: any[]) =>
            index === self.findIndex((t: any) => t.tags_name === tag.tags_name)
          )
          .sort((a: any, b: any) => a.tags_name.localeCompare(b.tags_name));

        setAlignmentLocationOptions(uniqueAlignmentLocationList.map((tag: any) => ({ id: tag.tags_id, value: tag.tags_name, label: tag.tags_name })));
      } catch (error) {
        // Set fallback options if API calls fail
        // setProcessTypeOptions([
        //   { value: 'GMAW', label: 'GMAW' },
        //   { value: 'FCAW', label: 'FCAW' },
        //   { value: 'SMAW', label: 'SMAW' },
        //   { value: 'GTAW', label: 'GTAW' },
        //   { value: 'SAW', label: 'SAW' }
        // ]);
        // setWeldPositionOptions([
        //   { value: '1G-Flat', label: '1G - Flat' },
        //   { value: '2G-Horizontal', label: '2G - Horizontal' },
        //   { value: '3G-Vertical', label: '3G - Vertical' },
        //   { value: '4G-Overhead', label: '4G - Overhead' },
        //   { value: '5G-Pipe Vertical', label: '5G - Pipe Vertical' },
        //   { value: '6G-Pipe 45°', label: '6G - Pipe 45°' }
        // ]);
        // setJointTypeOptions([
        //   { value: 'Butt Joint', label: 'Butt Joint' },
        //   { value: 'Lap Joint', label: 'Lap Joint' },
        //   { value: 'T-Joint', label: 'T-Joint' },
        //   { value: 'Corner Joint', label: 'Corner Joint' },
        //   { value: 'Edge Joint', label: 'Edge Joint' },
        //   { value: 'J-Prep', label: 'J-Prep' }
        // ]);
      }
    };

    if(isOpen) {
      // Always load initial data when component mounts
      loadInitialData();
    }
  }, [isOpen]);





  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
   // setEditedDetails(specification.files[0]?.details || {});
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!onUpdate) return;
    if (specification.type === 'PASS_CONFIG' && editedDetails.passConfig.passes.length > 0) {
      editedDetails.passConfig.passes.forEach((pass: any, index: any) => {
        if (!pass.name) {
          customNotification.warning(`Please add a  name of pass ${index + 1} `);
          return;
        }
      });
    }

    if (specification.type === 'FITMENT_READINGS') {
      const fitmentReadingsDetails = editedDetails.fitmentReadings;
      if (isSpoolsSpecification()) {
        const spoolsMinMaxPairs: { min: unknown; max: unknown; label: string }[] = [
          {
            min: fitmentReadingsDetails?.internalHiLowMin,
            max: fitmentReadingsDetails?.internalHiLowMax,
            label: 'internal hi-low',
          },
          {
            min: fitmentReadingsDetails?.wallThicknessTopRangeMin,
            max: fitmentReadingsDetails?.wallThicknessTopRangeMax,
            label: 'wall thickness top range',
          },
          {
            min: fitmentReadingsDetails?.gapToleranceMin,
            max: fitmentReadingsDetails?.gapToleranceMax,
            label: 'gap tolerance range',
          },
        ];

        for (const pair of spoolsMinMaxPairs) {
          if (
            !isBlankValue(pair.min) &&
            !isBlankValue(pair.max) &&
            Number(pair.min) >= Number(pair.max)
          ) {
            customNotification.warning(`Minimum ${pair.label} cannot be greater than maximum ${pair.label}`);
            return;
          }
        }
      } else {
      if (!fitmentReadingsDetails?.numberOfReadings) {
        customNotification.warning('Please add a number of readings');
        return;
      }

      if (!fitmentReadingsDetails?.landMin) {
        customNotification.warning('Please add a land minimum');
        return;
      }
      if (!fitmentReadingsDetails?.landMax) {
        customNotification.warning('Please add a land maximum');
        return;
      }
      if (!fitmentReadingsDetails?.wallThicknessMin) {
        customNotification.warning('Please add a wall thickness minimum');
        return;
      }
      if (!fitmentReadingsDetails?.wallThicknessMax) {
        customNotification.warning('Please add a wall thickness max');
        return;
      }
      if (!fitmentReadingsDetails?.internalHiLowMin) {
        customNotification.warning('Please add an internal hi-low minimum');
        return;
      }
      if (!fitmentReadingsDetails?.internalHiLowMax) {
        customNotification.warning('Please add an internal hi-low maximum');
        return;
      }
      if (!fitmentReadingsDetails?.externalHiLowMin) {
        customNotification.warning('Please add an external hi-low minimum');
        return;
      }
      if (!fitmentReadingsDetails?.externalHiLowMax) {
        customNotification.warning('Please add an external hi-low maximum');
        return;
      }
      if (!fitmentReadingsDetails?.bevelTopRangeMin) {
        customNotification.warning('Please add a bevel top range minimum');
        return;
      }
      if (!fitmentReadingsDetails?.bevelTopRangeMax) {
        customNotification.warning('Please add a bevel top range maximum');
        return;
      }

      if (Number(fitmentReadingsDetails?.wallThicknessMin) >= Number(fitmentReadingsDetails?.wallThicknessMax)) {
        customNotification.warning('Minimum wall thickness cannot be greater than maximum wall thickness');
        return;
      }
      if (Number(fitmentReadingsDetails?.landMax) <= Number(fitmentReadingsDetails?.landMin)) {
        customNotification.warning('Maximum land cannot be less than minimum land');
        return;
      }
      if (Number(fitmentReadingsDetails?.internalHiLowMin) >= Number(fitmentReadingsDetails?.internalHiLowMax)) {
        customNotification.warning('Minimum internal hi-low cannot be greater than maximum internal hi-low');
        return;
      }
      if (Number(fitmentReadingsDetails?.externalHiLowMin) >= Number(fitmentReadingsDetails?.externalHiLowMax)) {
        customNotification.warning('Minimum external hi-low cannot be greater than maximum external hi-low');
        return;
      }
      if (Number(fitmentReadingsDetails?.bevelTopRangeMin) >= Number(fitmentReadingsDetails?.bevelTopRangeMax)) {
        customNotification.warning('Minimum bevel top range cannot be greater than maximum bevel top range');
        return;
      }
      }
    }

    if (specification.type === 'WPS' && isSpoolsSpecification()) {
      const preheat = editedDetails?.preheatTemperature;
      if (isBlankValue(preheat?.minC)) {
        customNotification.warning('Please enter preheat temperature minimum (°C)');
        return;
      }
      if (isBlankValue(preheat?.maxC)) {
        customNotification.warning('Please enter preheat temperature maximum (°C)');
        return;
      }
      if (Number(preheat?.minC) >= Number(preheat?.maxC)) {
        customNotification.warning('Preheat temperature minimum must be less than maximum');
        return;
      }
    }


    // Validation for ALIGNMENT type
    if (specification?.type === 'ALIGNMENT') {
      const alignmentDetails = editedDetails.alignment;

      // Validate Number of Locations (2-10)
      if (!alignmentDetails?.numberOfAxialAlignments ||
        alignmentDetails.numberOfAxialAlignments < 2 ||
        alignmentDetails.numberOfAxialAlignments > 10) {
        customNotification.warning('Number of Locations must be between 2 and 10');
        return;
      }

      // Validate Alignment Location is selected
      if (!alignmentDetails?.alignmentLocation ||
        alignmentDetails.alignmentLocation.length === 0) {
        customNotification.warning('Please select at least one Alignment Location');
        return;
      }

      // Validate Note is selected
      if (!alignmentDetails?.note ||
        alignmentDetails.note.length === 0) {
        customNotification.warning('Please select at least one Note');
        return;
      }
    }

    if (specification.type === 'PASS_CONFIG') {
      const passConfigDetails = editedDetails.passConfig;
      if (!passConfigDetails?.passes) {
        customNotification.warning('Please add a pass');
        return;
      }

      for (let i = 0; i < passConfigDetails.passes.length; i++) {
        const pass = passConfigDetails.passes[i];
        if (!pass.name) {
          customNotification.warning(`Please add a name for pass ${i + 1}`);
          return;
        }
        if (!pass.numberOfPasses || isNaN(Number(pass.numberOfPasses))) {
          customNotification.warning(`Please add a valid number of passes for pass ${i + 1}`);
          return;
        }
      }
    }



    const values = {...editedDetails};
    if(specification.type === 'FITMENT_READINGS'){
      const fitmentReadingsDetails = values?.fitmentReadings;
     
      values.fitmentReadings = convertFitmentReadingsObjectToInches(fitmentReadingsDetails);
      console.log(values.fitmentReadings);
    }



    setIsSaving(true);
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    try {
      const updatedSpec = {
        type: specification.type as SpecificationDocument['type'],
        pipeId: Number(specification.pipeId),
        projectId: specification.projectId,
        projectTypeId: specification.projectTypeId,
        docId: specification.docRefId,
        uploadDate: new Date().toISOString(),
        uploadedBy: user.email || 'no email',
        details: values
      };
      updatedSpec.uploadDate = new Date().toISOString();
      //  await onUpdate(updatedSpec);
      const response = await AddSpecification([updatedSpec], false)
      if (response) {
        Notification.success('Specification updated successfully', `The ${SPEC_TYPE_LABELS[specification.type]} document has been updated.`);
        onUpdate(specification);
      }
      setIsEditing(false);
    } catch (error) {
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field: string, value: any) => {
    setEditedDetails((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const updateNestedField = (parentField: string, field: string, value: any) => {
    setEditedDetails((prev: any) => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [field]: value
      }
    }));
  };


  const updateAlignmentField = (field: string, value: any) => {
    setEditedDetails((prev: any) => {
      const updatedAlignment = {
        ...prev.alignment,
        [field]: value
      };

      // If updating numberOfAxialAlignments, auto-populate default degrees
      if (field === 'numberOfAxialAlignments') {
        if (value > 0) {
          const defaultDegrees = Array.from({ length: value }, (_, index) =>
            (index * 90) % 360
          );
          updatedAlignment.degrees = defaultDegrees;
        } else {
          // Clear degrees if number is 0 or negative
          updatedAlignment.degrees = [];
        }
      }

      return {
        ...prev,
        alignment: updatedAlignment
      };
    });
  };
  const updateAlignmentDegreeField = (index: number, value: string) => {


    setEditedDetails((prev: any) => {


      const currentAlignment = prev.alignment || {};
      const currentDegrees = currentAlignment.degrees || [];
      const updatedDegrees = [...currentDegrees];


      // Allow empty string to clear the field
      if (value === '') {
        updatedDegrees[index] = '';

      } else {
        // Validate the degree value only if it's not empty
        const numValue = parseInt(value);
        if (!isNaN(numValue) && numValue >= 0 && numValue <= 360) {
          updatedDegrees[index] = numValue;

        } else {
          // If invalid, keep the current value (don't update)

          return prev;
        }
      }

      const newState = {
        ...prev,
        alignment: {
          ...currentAlignment,
          degrees: updatedDegrees
        }
      };

      return newState;
    });
  };

  const addPass = () => {
    const newPass = {
      name: 'Hot',
      numberOfPasses: 1
    };

    // Get the next available pass option that hasn't been used yet
    const usedPassNames = editedDetails?.passConfig?.passes.map((pass: any) => pass.name);
    const nextAvailablePass = passOptions.find(option => !usedPassNames.includes(option.value));
    if (nextAvailablePass) {
      newPass.name = nextAvailablePass.value;
    } else {
      newPass.name = passOptions[passOptions.length - 1].value;
    }

    const currentPasses = editedDetails?.passConfig?.passes || [];
    const updatedPasses = [...currentPasses, newPass];

    updateNestedField('passConfig', 'passes', updatedPasses);
    setNewPassName(''); // Reset the input after adding
  };

  const removePass = (index: number) => {
    const currentPasses = editedDetails?.passConfig?.passes || [];
    const updatedPasses = currentPasses.filter((_: any, i: number) => i !== index);

    updateNestedField('passConfig', 'passes', updatedPasses);
  };

  const updatePass = (index: number, field: string, value: any) => {
    const currentPasses = editedDetails?.passConfig?.passes || [];
    const updatedPasses = [...currentPasses];
    updatedPasses[index] = { ...updatedPasses[index], [field]: value };

    updateNestedField('passConfig', 'passes', updatedPasses);
  };

  // Alignment functions
  const addAlignmentLocation = () => {
    const currentLocations = editedDetails?.alignment?.alignmentLocation || [];
    const updatedLocations = [...currentLocations, ''];
    updateNestedField('alignment', 'alignmentLocation', updatedLocations);
  };

  const removeAlignmentLocation = (index: number) => {
    const currentLocations = editedDetails?.alignment?.alignmentLocation || [];
    const updatedLocations = currentLocations.filter((_: any, i: number) => i !== index);
    updateNestedField('alignment', 'alignmentLocation', updatedLocations);
  };

  const addAlignmentNote = () => {
    const currentNotes = editedDetails?.alignment?.note || [];
    const updatedNotes = [...currentNotes, ''];
    updateNestedField('alignment', 'note', updatedNotes);
  };

  const removeAlignmentNote = (index: number) => {
    const currentNotes = editedDetails?.alignment?.note || [];
    const updatedNotes = currentNotes.filter((_: any, i: number) => i !== index);
    updateNestedField('alignment', 'note', updatedNotes);
  };

  const addAlignmentDegree = () => {
    const currentDegrees = editedDetails?.alignment?.degrees || [];
    const updatedDegrees = [...currentDegrees, ''];
    updateNestedField('alignment', 'degrees', updatedDegrees);
  };

  const removeAlignmentDegree = (index: number) => {
    const currentDegrees = editedDetails?.alignment?.degrees || [];
    const updatedDegrees = currentDegrees.filter((_: any, i: number) => i !== index);
    updateNestedField('alignment', 'degrees', updatedDegrees);
  };

  // Weld Process functions
  const addProcessType = () => {
    setIsAddingProcessType(true);
  };



  const addWeldPosition = () => {
    setIsAddingWeldPosition(true);
  };


  const addJointType = () => {
    setIsAddingJointType(true);
  };



  const handleAddNote = (note: string) => {
    const currentNotes = editedDetails?.alignment?.note || [];
    const updatedNotes = [...currentNotes, note];
    updateAlignmentField('note', updatedNotes);
    setIsAddingNote(false);
  };





  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  const updateWeldProcessField = (field: string, value: string | string[]) => {
    setEditedDetails((prev: any) => ({
      ...prev,
      weldProcess: {
        ...prev.weldProcess,
        [field]: value
      }
    }));
  };


  // ... existing code ...
  // ... existing code ...
  // const handleAddAlignmentLocation = (location: string) => {
  //   const currentLocations = editedDetails?.alignment?.alignmentLocation || [];
  //   const updatedLocations = [...currentLocations, location];
  //   updateAlignmentField('alignmentLocation', updatedLocations);
  //   setIsAddingAlignmentLocation(false);
  // };

  const handleAddAlignmentLocation = async (newAlignmentLocation: string) => {
    if (newAlignmentLocation.trim()) {
      // Check if alignment location already exists
      if (alignmentLocationOptions.find(option => option.value === newAlignmentLocation.trim())) {
        customNotification.warning(`${newAlignmentLocation.trim()} already exists`);
        return;
      }

      // const newOption = { value: newAlignmentLocation, label: newAlignmentLocation };



      const apiPayload = {
        tags_name: newAlignmentLocation.trim(),
        project_id: specification.projectId,
        tags_type: 'ALIGNMENT_LOCATION_SPECS'
      }
      const res = await AddMaterialCategory(apiPayload);
      if (res) {
        setAlignmentLocationOptions([...alignmentLocationOptions, { value: res.tags_name, label: res.tags_name }]);
        const currentLocations = editedDetails?.alignment?.alignmentLocation || [];
        const updatedLocations = [...currentLocations, res.tags_name];
        updateAlignmentField('alignmentLocation', updatedLocations);
      }



      // Show success message
      // customNotification.success('Alignment Location Added', `${newAlignmentLocation.trim()} has been added successfully.`);
    }
  };


  const handleAddProcessType = async (processType: string) => {
    if (processTypeOptions.find((tag: any) => tag.tags_name === processType.trim())) {
      customNotification.warning(`${processType.trim()} tag already exists`);
      return;
    }
    const apiPayload = {
      tags_name: processType.trim(),
      project_id: specification.projectId,
      tags_type: 'WELD_PROCESS_SPECS'
    }
    const res = await AddMaterialCategory(apiPayload);
    if (res) {
      setProcessTypeOptions([...processTypeOptions, { id: res.tags_id, value: res.tags_name, label: res.tags_name }]);

      const currentTypes = editedDetails?.weldProcess?.processType || [];
      const updatedTypes = [...currentTypes, processType];
      updateWeldProcessField('processType', updatedTypes);
    }
    //setProcessTypeOptions([...processTypeOptions, { value: processType, label: processType }]);
    setIsAddingProcessType(false);
  };

  const handleAddWeldPosition = async (weldPosition: string) => {
    if (weldPositionOptions.find((tag: any) => tag.tags_name === weldPosition.trim())) {
      customNotification.warning(`${weldPosition.trim()} tag already exists`);
      return;
    }
    const apiPayload = {
      tags_name: weldPosition.trim(),
      project_id: specification.projectId,
      tags_type: 'WELD_POSITION_SPECS'
    }
    const res = await AddMaterialCategory(apiPayload);
    if (res) {
      setWeldPositionOptions([...weldPositionOptions, { id: res.tags_id, value: res.tags_name, label: res.tags_name }]);

      const currentPositions = editedDetails?.weldProcess?.weldPosition || [];
      const updatedPositions = [...currentPositions, weldPosition];
      updateWeldProcessField('weldPosition', updatedPositions);
    }
    //setWeldPositionOptions([...weldPositionOptions, { value: weldPosition, label: weldPosition }]);
    setIsAddingWeldPosition(false);
  };

  const handleAddJointType = async (jointType: string) => {
    if (jointTypeOptions.find((tag: any) => tag.tags_name === jointType.trim())) {
      customNotification.warning(`${jointType.trim()} tag already exists`);
      return;
    }
    const apiPayload = {
      tags_name: jointType.trim(),
      project_id: specification.projectId,
      tags_type: 'JOINT_TYPE_SPECS'
    }
    const res = await AddMaterialCategory(apiPayload);
    if (res) {
      setJointTypeOptions([...jointTypeOptions, { id: res.tags_id, value: res.tags_name, label: res.tags_name }]);

      const currentTypes = editedDetails?.weldProcess?.jointType || [];
      const updatedTypes = [...currentTypes, jointType];
      updateWeldProcessField('jointType', updatedTypes);
      //  setJointTypeOptions([...jointTypeOptions, { value: jointType, label: jointType }]);
    }
    setIsAddingJointType(false);
  };
  const renderRangeField = (
    label: string,
    minField: string,
    maxField: string,
    required = false
  ) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-gray-600">
            Minimum {required && <span className="text-red-500">*</span>}
          </Label>
          {isEditing ? (
            <Input
              type="number"
              step="0.01"
              value={editedDetails?.fitmentReadings?.[minField] ?? ''}
              onChange={(e) => updateNestedField('fitmentReadings', minField, toOptionalNumber(e.target.value))}
              placeholder="Min"
              className="h-8 text-sm"
            />
          ) : (
            <p className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
              {displayValue(editedDetails?.fitmentReadings?.[minField])}
            </p>
          )}
        </div>
        <div>
          <Label className="text-xs text-gray-600">
            Maximum {required && <span className="text-red-500">*</span>}
          </Label>
          {isEditing ? (
            <Input
              type="number"
              step="0.01"
              value={editedDetails?.fitmentReadings?.[maxField] ?? ''}
              onChange={(e) => updateNestedField('fitmentReadings', maxField, toOptionalNumber(e.target.value))}
              placeholder="Max"
              className="h-8 text-sm"
            />
          ) : (
            <p className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
              {displayValue(editedDetails?.fitmentReadings?.[maxField])}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const renderPreheatTemperatureDetails = () => (
    <div className="space-y-4">
      <h4 className="font-medium text-gray-900">Preheat Temperature Details</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
        <div className="space-y-2">
          <Label>Minimum (°C) <span className="text-red-500">*</span></Label>
          {isEditing ? (
            <Input
              type="number"
              step="1"
              value={editedDetails?.preheatTemperature?.minC ?? ''}
              onChange={(e) => updateNestedField('preheatTemperature', 'minC', toOptionalNumber(e.target.value))}
              placeholder="e.g. 100"
            />
          ) : (
            <p className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
              {displayValue(editedDetails?.preheatTemperature?.minC)}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label>Maximum (°C) <span className="text-red-500">*</span></Label>
          {isEditing ? (
            <Input
              type="number"
              step="1"
              value={editedDetails?.preheatTemperature?.maxC ?? ''}
              onChange={(e) => updateNestedField('preheatTemperature', 'maxC', toOptionalNumber(e.target.value))}
              placeholder="e.g. 150"
            />
          ) : (
            <p className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
              {displayValue(editedDetails?.preheatTemperature?.maxC)}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const renderFitmentReadingsDetails = () => {
    if (isSpoolsSpecification()) {
      return (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Fitment Readings Details</h4>
          <div className="grid grid-cols-2 gap-4">
            {renderRangeField(`Internal Hi-Low (${getUnitLabel(unit)})`, 'internalHiLowMin', 'internalHiLowMax')}
            {renderRangeField(`Wall Thickness Top Range (${getUnitLabel(unit)})`, 'wallThicknessTopRangeMin', 'wallThicknessTopRangeMax')}
            {renderRangeField(`Gap Tolerance Range (${getUnitLabel(unit)})`, 'gapToleranceMin', 'gapToleranceMax')}
          </div>
        </div>
      );
    }

    return (
    <div className="space-y-4">
      <h4 className="font-medium text-gray-900">Fitment Readings Details</h4>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Wall Thickness ({getUnitLabel(unit)})</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-gray-600">Minimum  <span className="text-red-500">*</span></Label>
              {isEditing ? (
                <Input
                  type="number"
                  step="0.01"
                  value={editedDetails?.fitmentReadings?.wallThicknessMin || ''}
                  onChange={(e) => updateNestedField('fitmentReadings', 'wallThicknessMin', Number(e.target.value))}
                  placeholder="Min"
                  className="h-8 text-sm"
                />
              ) : (
                <p className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
                  {editedDetails?.fitmentReadings?.wallThicknessMin || 'Not specified'}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs text-gray-600">Maximum  <span className="text-red-500">*</span></Label>
              {isEditing ? (
                <Input
                  type="number"
                  step="0.01"
                  value={editedDetails?.fitmentReadings?.wallThicknessMax || ''}
                  onChange={(e) => updateNestedField('fitmentReadings', 'wallThicknessMax', Number(e.target.value))}
                  placeholder="Max"
                  className="h-8 text-sm"
                />
              ) : (
                <p className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
                  {editedDetails?.fitmentReadings?.wallThicknessMax || 'Not specified'}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Land ({getUnitLabel(unit)})</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-gray-600">Minimum  <span className="text-red-500">*</span></Label>
              {isEditing ? (
                <Input
                  type="number"
                  step="0.01"
                  value={editedDetails?.fitmentReadings?.landMin || ''}
                  onChange={(e) => updateNestedField('fitmentReadings', 'landMin', Number(e.target.value))}
                  placeholder="Min"
                  className="h-8 text-sm"
                />
              ) : (
                <p className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
                  {editedDetails?.fitmentReadings?.landMin || 'Not specified'}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs text-gray-600">Maximum  <span className="text-red-500">*</span></Label>
              {isEditing ? (
                <Input
                  type="number"
                  step="0.01"
                  value={editedDetails?.fitmentReadings?.landMax || ''}
                  onChange={(e) => updateNestedField('fitmentReadings', 'landMax', Number(e.target.value))}
                  placeholder="Max"
                  className="h-8 text-sm"
                />
              ) : (
                <p className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
                  {editedDetails?.fitmentReadings?.landMax || 'Not specified'}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Internal Hi-Low ({getUnitLabel(unit)})</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-gray-600">Minimum  <span className="text-red-500">*</span></Label>
              {isEditing ? (
                <Input
                  type="number"
                  step="0.01"
                  value={editedDetails?.fitmentReadings?.internalHiLowMin || ''}
                  onChange={(e) => updateNestedField('fitmentReadings', 'internalHiLowMin', Number(e.target.value))}
                  placeholder="Min"
                  className="h-8 text-sm"
                />
              ) : (
                <p className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
                  {editedDetails?.fitmentReadings?.internalHiLowMin || 'Not specified'}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs text-gray-600">Maximum  <span className="text-red-500">*</span></Label>
              {isEditing ? (
                <Input
                  type="number"
                  step="0.01"
                  value={editedDetails?.fitmentReadings?.internalHiLowMax || ''}
                  onChange={(e) => updateNestedField('fitmentReadings', 'internalHiLowMax', Number(e.target.value))}
                  placeholder="Max"
                  className="h-8 text-sm"
                />
              ) : (
                <p className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
                  {editedDetails?.fitmentReadings?.internalHiLowMax || 'Not specified'}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <Label>External Hi-Low ({getUnitLabel(unit)})</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-gray-600">Minimum  <span className="text-red-500">*</span></Label>
              {isEditing ? (
                <Input
                  type="number"
                  step="0.01"
                  value={editedDetails?.fitmentReadings?.externalHiLowMin || ''}
                  onChange={(e) => updateNestedField('fitmentReadings', 'externalHiLowMin', Number(e.target.value))}
                  placeholder="Min"
                  className="h-8 text-sm"
                />
              ) : (
                <p className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
                  {editedDetails?.fitmentReadings?.externalHiLowMin || 'Not specified'}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs text-gray-600">Maximum  <span className="text-red-500">*</span></Label>
              {isEditing ? (
                <Input
                  type="number"
                  step="0.01"
                  value={editedDetails?.fitmentReadings?.externalHiLowMax || ''}
                  onChange={(e) => updateNestedField('fitmentReadings', 'externalHiLowMax', Number(e.target.value))}
                  placeholder="Max"
                  className="h-8 text-sm"
                />
              ) : (
                <p className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
                  {editedDetails?.fitmentReadings?.externalHiLowMax || 'Not specified'}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Bevel Top Range ({getUnitLabel(unit)})</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-gray-600">Minimum  <span className="text-red-500">*</span></Label>
              {isEditing ? (
                <Input
                  type="number"
                  step="0.01"
                  value={editedDetails?.fitmentReadings?.bevelTopRangeMin || ''}
                  onChange={(e) => updateNestedField('fitmentReadings', 'bevelTopRangeMin', Number(e.target.value))}
                  placeholder="Min"
                  className="h-8 text-sm"
                />
              ) : (
                <p className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
                  {editedDetails?.fitmentReadings?.bevelTopRangeMin || 'Not specified'}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs text-gray-600">Maximum  <span className="text-red-500">*</span></Label>
              {isEditing ? (
                <Input
                  type="number"
                  step="0.01"
                  value={editedDetails?.fitmentReadings?.bevelTopRangeMax || ''}
                  onChange={(e) => updateNestedField('fitmentReadings', 'bevelTopRangeMax', Number(e.target.value))}
                  placeholder="Max"
                  className="h-8 text-sm"
                />
              ) : (
                <p className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
                  {editedDetails?.fitmentReadings?.bevelTopRangeMax || 'Not specified'}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="space-y-2 pt-6">
          <Label>No of Readings  <span className="text-red-500">*</span></Label>
          {isEditing ? (
            <Input
              type="number"
              min="0"
              max="100"
              value={editedDetails?.fitmentReadings?.numberOfReadings || ''}
              onChange={(e) => updateNestedField('fitmentReadings', 'numberOfReadings', parseInt(e.target.value) || 0)}
              placeholder="Number of readings"
              className="h-8 text-sm"
            />
          ) : (
            <p className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
              {editedDetails?.fitmentReadings?.numberOfReadings || 'Not specified'}
            </p>
          )}
        </div>

      </div>
    </div>
  );
  };

  const renderPassConfigDetails = () => {

    return (
      <div className="space-y-4">



        <div className="flex items-center justify-between">
          <h4 className="font-medium">Pass Configuration</h4>
          {isEditing && <Button
            type="button"

            size="sm"
            onClick={addPass}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Pass
          </Button>
          }
        </div>

        <div className="space-y-4" style={{ height: '400px', overflow: 'auto' }}>
          {editedDetails?.passConfig?.passes?.map((pass: any, index: number) => (
            <div key={index} className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h5 className="font-medium">Pass {index + 1}</h5>
                <div className="flex items-center gap-2">
                  {isEditing && <Button
                    variant="outline"
                    size="sm"
                    onClick={() => removePass(index)}
                    disabled={editedDetails?.passConfig?.passes?.length <= 1}
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pass Name</Label>
                  {isEditing ? (
                    <Select
                      value={pass.name || ''}
                      onValueChange={(value: string) => updatePass(index, 'name', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select pass name" />
                      </SelectTrigger>
                      <SelectContent>
                        {[
                          { value: 'Hot', label: 'Hot' },
                          { value: 'Root', label: 'Root' },
                          { value: 'Fill', label: 'Fill' },
                          { value: 'Last Fill', label: 'Last Fill' },
                          { value: 'Strip', label: 'Strip' },
                          { value: 'Cap', label: 'Cap' },
                          { value: 'Disabled', label: 'Disabled' }
                        ].map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
                      {pass.name || 'Not specified'}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Number of Passes</Label>
                  {isEditing ? (
                    <Input
                      type="number"
                      value={pass.numberOfPasses || ''}
                      onChange={(e) => updatePass(index, 'numberOfPasses', parseInt(e.target.value))}
                      min={1}
                      max={20}
                      placeholder="Number of Passes"
                    />
                  ) : (
                    <p className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
                      {pass.numberOfPasses || 'Not specified'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* {isEditing && (
             <div className="flex items-center gap-2">
               <Input
                 type="text"
                 value={newPassName}
                 onChange={(e) => setNewPassName(e.target.value)}
                 placeholder="Enter pass name (optional)"
                 className="flex-1"
               />
               <Button 
                 onClick={addPass} 
                 disabled={editedDetails?.passConfig?.passes?.length >= 10}
                 className="flex items-center gap-2"
               >
                 <Plus className="h-4 w-4" />
                 Add Pass
               </Button>
             </div>
           )} */}

          {!editedDetails?.passConfig?.passes || editedDetails.passConfig.passes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No pass configuration details available.</p>
              {isEditing && (
                <Button
                  onClick={addPass}
                  variant="outline"
                  className="mt-4"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Pass
                </Button>
              )}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderAlignmentDetails = () => (
    <>
      <div className="space-y-4">

        <h4 className="font-medium text-gray-900">Axial Alignment Details</h4>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label>Number of Locations </Label>
            {isEditing ? (
              <Input
                type="number"
                min="2"
                max="10"
                value={editedDetails?.alignment?.numberOfAxialAlignments || ''}
                onChange={(e) => updateAlignmentField('numberOfAxialAlignments', parseInt(e.target.value))}
                placeholder="Number of Axial Alignments (2-10)"
              //  className="h-8 text-sm"
              />
            ) : (
              <p className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
                {editedDetails?.alignment?.numberOfAxialAlignments || 'Not specified'}
              </p>
            )}
          </div>





          <div className="space-y-2 col-span-3">

            <Label>Alignment Degrees</Label>

            {editedDetails?.alignment?.degrees && editedDetails.alignment.degrees.length > 0 ? (
              <div className={`grid gap-3 grid-cols-1 md:grid-cols-4`}>
                {Array.from({ length: editedDetails.alignment.numberOfAxialAlignments }, (_, index) => (
                  <div key={index} className="space-y-2">
                    {isEditing ? (
                      <div className="relative">
                        <Input
                          type="text"
                          value={editedDetails?.alignment?.degrees?.[index] ?? ''}
                          onChange={(e) => {
                            updateAlignmentDegreeField(index, e.target.value);
                          }}
                          placeholder={`${(index * 90) % 360}°`}
                          className="pr-8"
                        />
                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                          °
                        </span>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
                        {editedDetails.alignment.degrees[index] || '0'}°
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        <div className="space-y-2">
          <Label>Alignment Location</Label>
          {isEditing ? (
            <div className="space-y-2">
              <MultiSelect
                options={alignmentLocationOptions}
                selected={editedDetails?.alignment?.alignmentLocation || []}
                onChange={(selected: string[]) => updateAlignmentField('alignmentLocation', selected)}
                placeholder="Select alignment location"
                isSearchable={true}
                showAddButton={true}
                addButtonText="Add New Alignment Location"
                onAddButtonClick={() => setIsAddingAlignmentLocation(true)}
              />
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {editedDetails?.alignment?.alignmentLocation?.length > 0 ? (
                editedDetails.alignment.alignmentLocation.map((location: any, index: number) => (
                  <p key={index} className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
                    {alignmentLocationOptions?.find((option: any) => option.value === location)?.label || location}
                  </p>
                ))
              ) : (
                <p className="text-sm text-gray-500">No locations specified</p>
              )}
            </div>
          )}
        </div>
        <div className="space-y-2 col-span-2">
          <div className="flex items-center justify-between">
            <Label>Notes</Label>
          </div>
          {isEditing ? (
            <div className="space-y-2">

              <Input
                id="note"
                value={editedDetails?.alignment?.note || ''}
                onChange={(e) => updateAlignmentField('note', e.target.value)}
                placeholder="Enter Note"
              />
              {/* <MultiSelect
                options={noteOptions}
                selected={editedDetails?.alignment?.note || []}
                onChange={(selected: string[]) => updateAlignmentField('note', selected)}
                placeholder="Select note"
                isSearchable={false}
                showAddButton={true}
                addButtonText="Add New Note"
                onAddButtonClick={() => setIsAddingNote(true)}
              /> */}
            </div>
          ) : (
            <div className="space-y-1">
              {editedDetails?.alignment?.note ? (

                <p className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
                  {editedDetails.alignment.note}
                </p>

              ) : (
                <p className="text-sm text-gray-500">No notes specified</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add New Alignment Location Dialog */}
      <AddNewItemDialog
        open={isAddingAlignmentLocation}
        onOpenChange={setIsAddingAlignmentLocation}
        title="Add New Alignment Location"
        label="Alignment Location"
        placeholder="Enter alignment location"
        onAdd={handleAddAlignmentLocation}
        buttonText="Add Alignment Location"
        cancelText="Cancel"
      />
      {/* Add New Note Dialog */}
      <AddNewItemDialog
        open={isAddingNote}
        onOpenChange={setIsAddingNote}
        title="Add New Note"
        label="Note"
        placeholder="Enter note"
        onAdd={handleAddNote}
        buttonText="Add Note"
        cancelText="Cancel"
      />
    </>
  );

  const renderWeldProcessDetails = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">Welding Process Details</h4>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Process Type</Label>
          {isEditing ? (
            <MultiSelect
              options={processTypeOptions}
              selected={Array.isArray(editedDetails?.weldProcess?.processType) ? editedDetails.weldProcess.processType : []}
              onChange={(selected: string[]) => updateWeldProcessField('processType', selected)}
              placeholder="Select process types"
              isSearchable={true}
              showAddButton={true}
              addButtonText="Add New Process Type"
              onAddButtonClick={() => addProcessType()}
            />
          ) : (
            <div className="space-y-1">
              {Array.isArray(editedDetails?.weldProcess?.processType) && editedDetails.weldProcess.processType.length > 0 ? (
                editedDetails.weldProcess.processType.map((type: string, index: number) => (
                  <p key={index} className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
                    {processTypeOptions?.find((option: any) => option.value === type)?.label || type}
                  </p>
                ))
              ) : (
                <p className="text-sm text-gray-500">No process types specified</p>
              )}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label>Weld Position</Label>
          {isEditing ? (
            <MultiSelect
              options={weldPositionOptions}
              selected={Array.isArray(editedDetails?.weldProcess?.weldPosition) ? editedDetails.weldProcess.weldPosition : []}
              onChange={(selected: string[]) => updateWeldProcessField('weldPosition', selected)}
              placeholder="Select weld positions"
              isSearchable={true}
              showAddButton={true}
              addButtonText="Add New Weld Position"
              onAddButtonClick={() => addWeldPosition()}
            />
          ) : (
            <div className="space-y-1">
              {Array.isArray(editedDetails?.weldProcess?.weldPosition) && editedDetails.weldProcess.weldPosition.length > 0 ? (
                editedDetails.weldProcess.weldPosition.map((position: string, index: number) => (
                  <p key={index} className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
                    {weldPositionOptions?.find((option: any) => option.value === position)?.label || position}
                  </p>
                ))
              ) : (
                <p className="text-sm text-gray-500">No weld positions specified</p>
              )}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label>Joint Type</Label>
          {isEditing ? (
            <MultiSelect
              options={jointTypeOptions}
              selected={Array.isArray(editedDetails?.weldProcess?.jointType) ? editedDetails.weldProcess.jointType : []}
              onChange={(selected: string[]) => updateWeldProcessField('jointType', selected)}
              placeholder="Select joint types"
              isSearchable={true}
              showAddButton={true}
              addButtonText="Add New Joint Type"
              onAddButtonClick={() => addJointType()}
            />
          ) : (
            <div className="space-y-1">
              {Array.isArray(editedDetails?.weldProcess?.jointType) && editedDetails.weldProcess.jointType.length > 0 ? (
                editedDetails.weldProcess.jointType.map((type: string, index: number) => (
                  <p key={index} className="text-sm text-gray-700 p-2 bg-gray-50 rounded">
                    {jointTypeOptions?.find((option: any) => option.value === type)?.label || type}
                  </p>
                ))
              ) : (
                <p className="text-sm text-gray-500">No joint types specified</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add New Process Type Dialog */}
      <AddNewItemDialog
        open={isAddingProcessType}
        onOpenChange={setIsAddingProcessType}
        title="Add New Process Type"
        label="Process Type"
        placeholder="Enter process type"
        onAdd={handleAddProcessType}
        buttonText="Add Process Type"
        cancelText="Cancel"
      />
      {/* Add New Weld Position Dialog */}
      <AddNewItemDialog
        open={isAddingWeldPosition}
        onOpenChange={setIsAddingWeldPosition}
        title="Add New Weld Position"
        label="Weld Position"
        placeholder="Enter weld position"
        onAdd={handleAddWeldPosition}
        buttonText="Add Weld Position"
        cancelText="Cancel"
      />
      {/* Add New Joint Type Dialog */}
      <AddNewItemDialog
        open={isAddingJointType}
        onOpenChange={setIsAddingJointType}
        title="Add New Joint Type"
        label="Joint Type"
        placeholder="Enter joint type"
        onAdd={handleAddJointType}
        buttonText="Add Joint Type"
        cancelText="Cancel"
      />
    </div>
  );



  const renderSpecificationDetails = () => {
    switch (specification.type) {

      case 'WELD_PROCESS':
        return renderWeldProcessDetails();

      case 'ALIGNMENT':
        return renderAlignmentDetails();
      case 'PASS_CONFIG':
        return renderPassConfigDetails();
      case 'FITMENT_READINGS':
        return renderFitmentReadingsDetails();

      case 'WPS':
        if (isSpoolsSpecification()) {
          return renderPreheatTemperatureDetails();
        }
        return (
          <div className="text-center py-8 text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>Details view not available for this specification type.</p>
          </div>
        );

      default:
        return (
          <div className="text-center py-8 text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>Details view not available for this specification type.</p>
          </div>
        );
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onCloseModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {specification.fileName || 'Specification Details'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1  space-y-6 p2">
            {/* Header Information */}
            <Card>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-600">Type:</span>
                    <span className="ml-2 text-gray-900">{SPEC_TYPE_LABELS[specification.type as keyof typeof SPEC_TYPE_LABELS]}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Updated Date:</span>
                    <span className="ml-2 text-gray-900">{formatDate(selectedSpecification?.uploadDate || '')}</span>
                  </div>
                  {/* {selectedSpecification?.uploadedBy && (
                    <div>
                      <span className="font-medium text-gray-600">Uploaded By:</span>
                      <span className="ml-2 text-gray-900">{selectedSpecification?.uploadedBy}</span>
                    </div>
                  )}
                  {specification.version && (
                    <div>
                      <span className="font-medium text-gray-600">Version:</span>
                      <span className="ml-2 text-gray-900">{specification.version}</span>
                    </div>
                  )} */}
                </div>
              </CardContent>
            </Card>

            {/* Specification Details */}
            <Card>
              <CardContent className="p-4">
                {renderSpecificationDetails()}
              </CardContent>
            </Card>

            {/* File Information */}
            {editedDetails.fileName && editedDetails?.files && editedDetails?.files.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Files</h4>
                  <div className="space-y-2">
                    {specification.files.map((file: any, index: number) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-700">{file.fileName}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(file.fileUrl, '_blank')}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <DialogFooter className="gap-3 mt-6 flex-shrink-0">
            {!isEditing ? (
              <>
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
                {/* <Button onClick={handleDownload} variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button> */}
                {onUpdate && canEdit && (
                  <Button onClick={handleEdit}
                  >
                    <Edit3 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button variant="outline" onClick={handleCancelEdit}>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={isSaving} loading={isSaving}
                  loadingText="Saving...">
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Download Modal for multiple files */}
      <DocumentDownloadModal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        documents={specification.files || []}
        onDownload={(document) => window.open(document.fileUrl, '_blank')}
        title={`Download ${specification.type} Files`}
      />
    </>
  );
};
